import { useCallback, useEffect } from "react";

// reCAPTCHA — сторонний скрипт с google.com. Он бывает недоступен (блокировки,
// слабая сеть, блокировщики рекламы), поэтому ждём его строго ограниченное
// время. Прежние 5 секунд отсчитывались только от самого execute(), а если
// скрипт ещё не загрузился, хук сдавался мгновенно — на свежеоткрытом экране
// это самый частый случай. Теперь в бюджет входит и загрузка.
const RECAPTCHA_TIMEOUT_MS = 8000;

// Скрипт грузится один раз на всё приложение: раньше каждый экземпляр хука
// добавлял в <head> свой тег, и на экране с двумя формами их оказывалось два.
const loaders = new Map<string, Promise<boolean>>();

const whenReady = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (!window.grecaptcha) return resolve(false);
    // execute() до ready() не гарантирован — внутренности могут быть ещё не
    // проинициализированы, даже когда объект уже есть.
    if (typeof window.grecaptcha.ready !== "function") return resolve(true);
    window.grecaptcha.ready(() => resolve(true));
  });

const loadRecaptcha = (siteKey: string): Promise<boolean> => {
  const started = loaders.get(siteKey);
  if (started) return started;

  const loading = new Promise<boolean>((resolve) => {
    if (window.grecaptcha) return resolve(true);

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  })
    .then((loaded) => (loaded ? whenReady() : false))
    .then((ready) => {
      // Неудачу не кэшируем: со следующей попыткой сеть может уже появиться.
      if (!ready) loaders.delete(siteKey);
      return ready;
    });

  loaders.set(siteKey, loading);
  return loading;
};

const withTimeout = <T,>(work: Promise<T>, ms: number): Promise<T | null> =>
  Promise.race([
    work,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
  ]);

const useRecaptchaV3 = (siteKey: string) => {
  // Прогреваем заранее, чтобы к моменту отправки формы скрипт уже был готов.
  useEffect(() => {
    void loadRecaptcha(siteKey);
  }, [siteKey]);

  // Невозможность получить токен не должна ломать отправку формы: раньше
  // reject или зависание execute() всплывали в вызывающий код и оставляли
  // формы в состоянии "отправляется" навсегда — кнопка залипала disabled.
  // Сервер отличает «токена нет» от «это бот» и сам решает по шагу.
  return useCallback(
    (action: string): Promise<string | null> =>
      withTimeout(
        (async () => {
          try {
            const ready = await loadRecaptcha(siteKey);
            if (!ready) return null;

            return await window.grecaptcha.execute(siteKey, { action });
          } catch (error) {
            console.warn("reCAPTCHA execute failed", error);
            return null;
          }
        })(),
        RECAPTCHA_TIMEOUT_MS
      ),
    [siteKey]
  );
};

export default useRecaptchaV3;
